import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgxMaskDirective } from 'ngx-mask';
import { PeopleComponent } from '../../pages/people/people.component';
import { AddressService } from '../../services/address.service';
import { ZipcodeResponse } from '../../model/zipcode-response';
import { FormStateService } from '../../services/form-state.service';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';

@Component({
  selector: 'app-address-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    NgxMaskDirective,
    ToastModule,
  ],
  providers: [MessageService],
  template: `
    <!-- BOTÕES - SALVAR E CARREGAR ENDEREÇO DO BENEFICIÁRIO -->
    <div class="same-address-button-container">
      <p-button
        label="Usar Endereço Salvo"
        icon="pi pi-copy"
        (onClick)="fillWithBeneficiaryAddress()"
        styleClass="p-button-secondary"
        pTooltip="Preencher com endereço salvo anteriormente"
      ></p-button>
    </div>

    <form [formGroup]="addressForm" class="address-form">
      <!-- CEP com botão de busca -->
      <div class="form-row">
        <div class="form-field full-width">
          <label for="zipcode" class="form-label">CEP</label>
          <div class="input-with-button">
            <input
              pInputText
              #zipcode
              id="zipcode"
              formControlName="zipcode"
              placeholder="00000-000"
              class="form-input"
              mask="99999-999"
              required="true"
              [ngClass]="{ 'error-input': isFieldInvalid('zipcode') }"
              (keyup.enter)="buscarCep(zipcode.value)"
              (keydown.enter)="moveToNextField('zipcode','buscarCep')"
            />
            <p-button
              label="Buscar"
              id="buscarCep"
              (onClick)="buscarCep(zipcode.value)"
              styleClass="search-button"
              (keydown.enter)="moveToNextField('buscarCep','street')"
            ></p-button>
          </div>
          <div *ngIf="isFieldInvalid('zipcode')" class="error-message">
            O CEP é obrigatório.
          </div>
        </div>
      </div>

      <!-- Rua -->
      <div class="form-row">
        <div class="form-field full-width">
          <label for="street" class="form-label">Rua</label>
          <input
            pInputText
            id="street"
            formControlName="street"
            placeholder="Nome da rua"
            class="form-input"
            required="true"
            [ngClass]="{ 'error-input': isFieldInvalid('street') }"
            (keydown.enter)="moveToNextField('street','number')"
          />
          <div *ngIf="isFieldInvalid('street')" class="error-message">
            O logradouro é obrigatório.
          </div>
        </div>
      </div>

      <!-- Número -->
      <div class="form-row">
        <div class="form-field full-width">
          <label for="number" class="form-label">Número</label>
          <input
            pInputText
            id="number"
            formControlName="number"
            placeholder="Número"
            class="form-input"
            required="true"
            [ngClass]="{ 'error-input': isFieldInvalid('number') }"
            (keydown.enter)="moveToNextField('number','compliment')"
          />
          <div *ngIf="isFieldInvalid('number')" class="error-message">
            O número é obrigatório.
          </div>
        </div>
      </div>

      <!-- Complemento e Referência -->
      <div class="form-row">
        <div class="form-field half-width">
          <label for="compliment" class="form-label">Complemento</label>
          <input
            pInputText
            id="compliment"
            formControlName="compliment"
            placeholder="Apartamento, bloco, etc."
            class="form-input"
            [ngClass]="{ 'error-input': isFieldInvalid('compliment') }"
            (keydown.enter)="moveToNextField('compliment','reference')"
          />
        </div>
        <div class="form-field half-width">
          <label for="reference" class="form-label">Referência</label>
          <input
            pInputText
            id="reference"
            formControlName="reference"
            placeholder="Próximo a..."
            class="form-input"
            [ngClass]="{ 'error-input': isFieldInvalid('reference') }"
            (keydown.enter)="moveToNextField('reference','neighborhood')"
          />
        </div>
      </div>

      <!-- Bairro -->
      <div class="form-row">
        <div class="form-field full-width">
          <label for="neighborhood" class="form-label">Bairro</label>
          <input
            pInputText
            id="neighborhood"
            formControlName="neighborhood"
            placeholder="Nome do bairro"
            class="form-input"
            required="true"
            [ngClass]="{ 'error-input': isFieldInvalid('neighborhood') }"
            (keydown.enter)="moveToNextField('neighborhood','city')"
          />
          <div *ngIf="isFieldInvalid('neighborhood')" class="error-message">
            O bairro é obrigatório.
          </div>
        </div>
      </div>

      <!-- Município e UF -->
      <div class="form-row">
        <div class="form-field half-width">
          <label for="city" class="form-label">Município</label>
          <input
            pInputText
            id="city"
            formControlName="city"
            placeholder="Nome da cidade"
            class="form-input"
            required="true"
            [ngClass]="{ 'error-input': isFieldInvalid('city') }"
            (keydown.enter)="moveToNextField('city','state')"
          />
          <div *ngIf="isFieldInvalid('city')" class="error-message">
            O município é obrigatório.
          </div>
        </div>
        <div class="form-field half-width">
          <label for="state" class="form-label">UF</label>
          <input
            pInputText
            id="state"
            formControlName="state"
            maxlength="2"
            placeholder="UF"
            class="form-input"
            required="true"
            [ngClass]="{ 'error-input': isFieldInvalid('state') }"
            (keyup.enter)="onSave(dialogConfig.data?.address?.id, addressForm.value)"
            (keydown.enter)="moveToNextField('state','save')"
          />
          <div *ngIf="isFieldInvalid('state')" class="error-message">
            A UF é obrigatória.
          </div>
        </div>
      </div>
    </form>

    <div class="dialog-actions">
      <p-button label="Cancelar" icon="pi pi-times" (onClick)="onCancel()" styleClass="p-button-secondary"></p-button>
      <p-button
        label="Salvar"
        icon="pi pi-check"
        id="save"
        (onClick)="onSave(dialogConfig.data?.address?.id, addressForm.value)"
        styleClass="p-button-primary"
      ></p-button>
    </div>
`,
  styles: [
    `
    .same-address-button-container {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 16px;
      padding: 0 16px;
      gap: 8px;
      flex-wrap: wrap;
    }

    .same-address-button {
      display: flex;
      align-items: center;
      gap: 8px;
      border-radius: 4px;
      padding: 8px 16px;
      font-weight: 500;
      transition: background-color 0.3s;
    }

    /* Form Styles */
    .address-form {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .form-row {
      display: flex;
      gap: 16px;
      align-items: flex-start;
    }

    .form-field {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .full-width {
      width: 100%;
    }

    .half-width {
      flex: 1;
    }

    .form-label {
      font-weight: 600;
      color: #374151;
      font-size: 14px;
      margin-bottom: 4px;
    }

    .form-input {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid #d1d5db;
      border-radius: 6px;
      font-size: 14px;
      transition: border-color 0.2s, box-shadow 0.2s;
    }

    .form-input:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    .input-with-button {
      display: flex;
      gap: 8px;
      align-items: stretch;
    }

    .input-with-button .form-input {
      flex: 1;
    }

    .input-with-button .search-button {
      flex-shrink: 0;
      height: auto;
      padding: 8px 16px;
      background-color: #10b981;
      color: white;
      border: none;
      border-radius: 6px;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 0.2s;
    }

    .input-with-button .search-button:hover {
      background-color: #059669;
    }

    .error-input {
      border-color: #ef4444 !important;
      background-color: #fef2f2;
    }

    .error-message {
      color: #ef4444;
      font-size: 12px;
      font-weight: 500;
      margin-top: 2px;
    }

    .dialog-actions {
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      margin-top: 24px;
      padding-top: 16px;
      border-top: 1px solid #e5e7eb;
    }
    `
  ]
})
export class AddressDialogComponent implements OnInit {
  addressForm: FormGroup;
  zipcode: string = '';
  showDebug: boolean = false; // Defina como true para ver botão de debug

  @ViewChild('zipcode') zipcodeInputField!: ElementRef;

  constructor(
    private addressService: AddressService,
    private messageService: MessageService,
    private fb: FormBuilder,
    public dialogRef: DynamicDialogRef,
    private cdr: ChangeDetectorRef,
    private formStateService: FormStateService,
    public dialogConfig: DynamicDialogConfig
  ) {
    this.addressForm = this.fb.group({
      street: ['', Validators.required],
      number: ['', Validators.required],
      compliment: [''],
      reference: [''],
      neighborhood: ['', Validators.required],
      city: ['', Validators.required],
      state: ['', Validators.required],
      zipcode: ['', [Validators.required]],
    });
  }

  ngOnInit() {
    // Preenche o formulário com os dados do endereço atual
    if (this.dialogConfig.data && this.dialogConfig.data.address) {
      this.addressForm.patchValue({
        zipcode: this.dialogConfig.data.address.zipcode || '',
        street: this.dialogConfig.data.address.street || '',
        number: this.dialogConfig.data.address.number || '',
        compliment: this.dialogConfig.data.address.compliment || '',
        reference: this.dialogConfig.data.address.reference || '',
        neighborhood: this.dialogConfig.data.address.neighborhood || '',
        city: this.dialogConfig.data.address.city || '',
        state: this.dialogConfig.data.address.state || '',
      });
    }

    // Subscribe to zipcode changes
    this.addressForm.get('zipcode')?.valueChanges.subscribe((zipcode) => {
      if (zipcode && zipcode.length === 9) {
        this.fetchAddressByZipcode(zipcode);
      }
    });

    setTimeout(() => {
      this.zipcodeInputField.nativeElement.focus();
    }, 0);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(addressId: number, formData: any): void {
    if (this.addressForm.valid) {
      this.addressService.setAddressData(addressId, formData).subscribe({
        next: (response) => {
          // Salva os dados no serviço de estado
          this.formStateService.saveFormData(`address_${addressId}`, formData);

          // TAMBÉM SALVA COMO ENDEREÇO DO BENEFICIÁRIO
          this.formStateService.saveFormData('saved_address', formData);

          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Endereço atualizado com sucesso!'
          });

          this.dialogRef.close({
            saved: true,
            person: this.dialogConfig.data?.person
          });
        },
        error: (error) => {
          // Salva os dados mesmo em caso de erro para recuperação posterior
          this.formStateService.saveFormData(`address_${addressId}_pending`, formData);

          let errorMessage = 'Erro ao atualizar o endereço.';
          if (error.status === 403) {
            errorMessage = 'Acesso negado. Verifique suas permissões.';
          } else if (error.status === 404) {
            errorMessage = 'Endereço não encontrado.';
          } else if (error.status === 400) {
            errorMessage = 'Dados inválidos. Verifique os campos do formulário.';
          }

          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: errorMessage
          });
        },
      });
    } else {
      // Salva dados do formulário inválido para correção posterior
      this.formStateService.saveFormData(`address_${this.dialogConfig.data?.address?.id}_invalid`, this.addressForm.value);

      this.messageService.add({
        severity: 'warn',
        summary: 'Aviso',
        detail: 'Formulário inválido. Verifique os campos.'
      });
    }
  }

  buscarCep(value: string) {
    const newzipcode = value.replace(/[^0-9]/g, '');

    if (newzipcode && newzipcode.length === 8) {
      this.addressService.getAddressByZipcode(newzipcode).subscribe({
        next: (response1: ZipcodeResponse) => {
          this.addressForm.patchValue({
            street: response1.logradouro,
            compliment: response1.complemento,
            neighborhood: response1.bairro,
            city: response1.localidade,
            state: response1.uf,
          });
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error fetching address:', error);
        },
      });
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Aviso',
        detail: 'Por favor, digite um CEP válido'
      });
    }
  }

  fetchAddressByZipcode(zipcode: string): void {
    this.addressService.getAddressByZipcode(zipcode).subscribe({
      next: (response: ZipcodeResponse) => {
        this.addressForm.patchValue({
          street: response.logradouro,
          compliment: response.complemento,
          neighborhood: response.bairro,
          city: response.localidade,
          state: response.uf,
        });
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error fetching address:', error);
      },
    });
  }

  cleanPhoneNumber(phoneNumber: string): string {
    return phoneNumber.replace(/[^0-9]/g, '');
  }

  moveToNextField(currentFieldId: string, nextFieldId: string): void {
    const control = this.addressForm.get(currentFieldId);

    if (control) {
      control.markAsTouched();
      control.updateValueAndValidity();
    }

    if (this.isFieldInvalid(currentFieldId)) {
      const currentField = document.getElementById(currentFieldId) as HTMLInputElement;
      currentField.focus();
    } else {
      const nextField = document.getElementById(nextFieldId) as HTMLElement;
      nextField?.focus();
    }
  }

  isFieldInvalid(field: string): boolean {
    const control = this.addressForm.get(field);
    return !!(control?.invalid && (control.touched || control.dirty));
  }

  fillWithBeneficiaryAddress(): void {
    // Tenta recuperar os dados do endereço do beneficiário salvo
    const beneficiaryAddress = this.formStateService.getFormData('saved_address');

    // console.log('Buscando endereço do beneficiário pela chave: saved_address');
    // console.log('Dados encontrados:', beneficiaryAddress);

    if (beneficiaryAddress) {
      // Preenche o formulário com os dados do beneficiário
      this.addressForm.patchValue(beneficiaryAddress);

      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Endereço carregado!'
      });
    } else {
      // Se não encontrar dados salvos
      this.messageService.add({
        severity: 'info',
        summary: 'Informação',
        detail: 'Nenhum endereço salvo encontrado.'
      });
    }
  }

  saveAsBeneficiaryAddress(): void {
    if (this.addressForm.valid) {
      const formData = this.addressForm.value;
      this.formStateService.saveFormData('saved_address', formData);

      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Endereço salvo como para uso posterior!'
      });
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Aviso',
        detail: 'Formulário inválido. Preencha todos os campos obrigatórios.'
      });
    }
  }

  showStoredData(): void {
    // Método para debugging - mostra todos os dados armazenados
    const allData = (this.formStateService as any).formDataStore;
    // console.log('Dados armazenados no FormStateService:', allData);

    const beneficiaryAddress = this.formStateService.getFormData('saved_address');
    // console.log('Endereço do beneficiário específico:', beneficiaryAddress);

    this.messageService.add({
      severity: 'info',
      summary: 'Informação',
      detail: 'Dados do console verificados. Veja o console do navegador.'
    });
  }
}
